import { CommonModule, TitleCasePipe, UpperCasePipe } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { environment } from '@core/environments/environment';
import { NotificationFacade } from '@core/facades/notification.facade';
import { Pagination } from '@core/models/pagination.model';
import { ApprovalService } from '@core/services/approval.service';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { Store } from '@ngxs/store';
import {
  DropdownComponent,
  DropDownModel,
} from '@shared/ui/dropdown/dropdown.component';
import { FiltersComponent } from '@shared/ui/filters/filters.component';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { IFILTER } from 'app/app.constants';
import { TableActions } from 'app/auth/table-store/table.actions';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { Subscription } from 'rxjs';
import { PermissionDirective } from "@shared/directives/permission.directive";
import { RolesAndPermissionsService } from '@core/services/roles-and-permissions.service';
import { TableRefreshComponent } from 'app/ui/table-refresh/table-refresh.component';

@Component({
  selector: 'app-approval-table',
  standalone: true,
  imports: [
    CommonModule,
    DropdownComponent,
    FormsModule,
    ReactiveFormsModule,
    TableNavigatorComponent,
    FiltersComponent,
    PermissionDirective,
    TableRefreshComponent
],
  templateUrl: './approval-table.component.html',
  styleUrl: './approval-table.component.scss',
  providers: [TitleCasePipe, UpperCasePipe]
})
export class ApprovalTableComponent {
  isLoading: boolean = false;
  filterMenuState: 'visible' | 'hidden' = 'hidden';
  approvals$;

  isDropdownVisible = true;
  showDropdown = false;
  subs: Subscription[] = [];
  documentList: any[] = [];
  actionList: DropDownModel[] = [];
  totalPending = 0;
  totalApproved = 0;
  totalReq = 0;
  totalCount!: number;
  date = new FormControl('', [Validators.required]);

  currentPage: number = 1;

  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  notify = inject(NotificationFacade);
  approval = inject(ApprovalService);
  change = inject(ChangeDetectorRef);
  breadCrumbService = inject(BreadCrumbService);
  private permissionService = inject(RolesAndPermissionsService);
  $index: any;

  constructor(private store: Store, private titleCasePipe: TitleCasePipe, private upperCasePipe: UpperCasePipe) {
    this.approvals$ = this.store.select(TableSelectors.getApprovalState);
  }

  isFilterActive: boolean = false;

  filterStructure: IFILTER[] = [
    {
      filterKey: 'start_date',
      label: 'Start Date',
      type: 'date',
    },
    {
      filterKey: 'end_date',
      label: 'End Date',
      type: 'date',
    },
    {
      filterKey: 'status',
      label: 'Status',
      type: 'dropdown',
      dropdown: [
        { key: 'pending', value: 'Pending' },
        { key: 'approved', value: 'Approved' },
        { key: 'rejected', value: 'Rejected' },
      ],
    },
  ];

  toggleFilterMenu() {
    this.filterMenuState =
      this.filterMenuState === 'hidden' ? 'visible' : 'hidden';
  }

  initDropdownList(param: any) {
    return (this.actionList = [
      {
        action: () => {
          this.openDocument(param);
        },
        title: 'Open Document',
        show: true,
      },
      {
        action: () => {
          this.approve(param);
        },
        title: 'Approve',
        show: param.status !== 'approved' && this.permissionService.hasPermission(['update_kyc_document_status']),
      },
      {
        action: () => {
          this.reject(param);
        },
        title: 'Reject',
        show: param.status !== 'rejected' && this.permissionService.hasPermission(['update_kyc_document_status']),
      },
      {
        action: () => this.viewAccountDetails(param),
        title: 'View Account',
        show: this.permissionService.hasPermission(['get_individual'])
      },
    ]);
  }

  approve(data: any) {
    const sub = this.approval.approveDocument(data.id).subscribe({
      next: async (resp: any) => {
        this.notify.success(resp.message);
        // Update state directly - no API call needed
        this.updateDocumentStatus(data.id, 'approved');
        this.updateMetaData('approved');
      },
    });
    this.subs.push(sub);
  }

  reject(data: any) {
    const sub = this.approval.rejectDocument(data.id).subscribe({
      next: async (resp: any) => {
        this.notify.success(resp.message);
        // Update state directly - no API call needed
        this.updateDocumentStatus(data.id, 'rejected');
        this.updateMetaData('rejected');
      },
    });
    this.subs.push(sub);
  }

  openDocument(data: any) {
    window.open(`${environment.FILE_BASE_URL}${data.link}`, '_blank');
  }

  toggle() {
    this.isDropdownVisible = !this.isDropdownVisible;
  }
  approvalsFilterEventListener() {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.documentList = [];
      let query = '';
      const currentPage = params['page'] ? parseInt(params['page']) : 1;

      this.currentPage = currentPage;
      const skip = (currentPage - 1) * 10;
      const size = 10;

      const queryParams = [];

      if (params[this.filterStructure[0].filterKey]) {
        queryParams.push(`start_date=${params['start_date']}`);
      }
      if (params[this.filterStructure[1].filterKey]) {
        queryParams.push(`end_date=${params['end_date']}`);
      }
      if (params[this.filterStructure[2].filterKey]) {
        queryParams.push(`status=${params['status']}`);
      }
      if (params['search']) {
        queryParams.push(`search_text=${params['search']}`);
      }

      query = queryParams.join('&');
      if (
        params[this.filterStructure[0].filterKey] ||
        params[this.filterStructure[1].filterKey] ||
        params[this.filterStructure[2].filterKey] ||
        params['search']
      ) {
        this.isFilterActive = true;
        this.isLoading = true;
        const sub = this.approval
          .filterDocumentsList(query, new Pagination({ skip, size }))
          .subscribe({
            next: (res: any) => {
              this.documentList = res.data.data;
              this.totalCount = res.data.total_count || 10;
              this.isLoading = false;
              this.change.detectChanges();
            },
            error: (err) => {
              this.isLoading = false;
              this.change.detectChanges();
            },
          });
        this.subs.push(sub);
      } else {
        this.isFilterActive = false;
        this.getApprovalList(new Pagination({ skip, size }));
      }
    });
  }

  getApprovalList(params: Pagination) {
    this.store.dispatch(new TableActions.LoadApprovals({ params }));

    this.documentList = [];
    this.isLoading = true;
    const sub = this.approval.getDocumentsList(params).subscribe({
      next: (resp: any) => {
        this.totalCount = resp.data.total_count;
        this.documentList = resp.data.data;
        this.isLoading = false;
        this.change.detectChanges();
      },
      error: (err) => {
        this.isLoading = false;
      },
    });
    this.subs.push(sub);
  }

  getDocumentsMetaData() {
    const sub = this.approval.getDocumentsMetaData().subscribe({
      next: (resp: any) => {
        this.totalReq = resp.data.total_docs;
        this.totalPending = resp.data.pendind_doc_count;
        this.totalApproved = resp.data.approved_doc_count;

        this.change.detectChanges();
      },
    });
    this.subs.push(sub);
  }

  viewAccountDetails(txn: any) {
    this.router.navigateByUrl(
      `/admin/accounts/${txn.account.account_type}/${txn.account_id}`
    );
  }

  navigate(currentPage: number = 1) {
    this.isLoading = true;
    this.currentPage = currentPage;
    this.router.navigate([], {
      queryParams: { page: currentPage },
      queryParamsHandling: 'merge',
    });
  }

  updateDocumentStatus(documentId: string, newStatus: 'approved' | 'rejected') {
    // Update the local documentList array immediately
    this.documentList = this.documentList.map((doc) => {
      if (doc.id === documentId) {
        return { ...doc, status: newStatus };
      }
      return doc;
    });

    // Dispatch action to update NgXs store
    this.store.dispatch(
      new TableActions.UpdateDocumentStatus({
        documentId,
        status: newStatus,
      })
    );

    // Trigger change detection to update the UI immediately
    this.change.detectChanges();
  }

  updateMetaData(actionType: 'approved' | 'rejected') {
    if (actionType === 'approved') {
      this.totalPending = Math.max(0, this.totalPending - 1);
      this.totalApproved = this.totalApproved + 1;
    } else if (actionType === 'rejected') {
      this.totalPending = Math.max(0, this.totalPending - 1);
    }

    this.change.detectChanges();
  }

  ngOnInit() {
    this.breadCrumbService.setPageBreadCrumb(this.activatedRoute);
    this.getDocumentsMetaData();

    this.approvalsFilterEventListener();
    this.store.dispatch(
      new TableActions.LoadApprovals({ params: new Pagination() })
    );

    this.subs.push(
      this.store.select(TableSelectors.getApprovals).subscribe((approvals) => {
        this.documentList = approvals;

        this.change.detectChanges();
      })
    );
    this.subs.push(
      this.store
        .select(TableSelectors.getApprovalLoading)
        .subscribe((loading) => {
          this.isLoading = loading;
          this.change.detectChanges();
        })
    );
  }

  formatPersonName(personalDetails: any): string {
    const firstName = this.titleCasePipe.transform(personalDetails.first_name) || '';
    const middleName = personalDetails.middle_name && personalDetails.middle_name !== '[null]' 
      ? this.titleCasePipe.transform(personalDetails.middle_name) 
      : '';
    const lastName = this.upperCasePipe.transform(personalDetails.last_name) || '';
    
    return `${firstName} ${middleName} ${lastName}`.replace(/\s+/g, ' ').trim();
  }

  getJointAccountName(account: any[]) {
    return account.reduce((p, c, i) => {
      const fullName = this.formatPersonName(c.personal_details);
      
      if (i > 0) {
        return `${p} & ${fullName}`;
      }
      return fullName;
    }, '');
  }

  getEditedJointAccountName(doc: any): string {
    if (!doc.account.joint_kyc || !Array.isArray(doc.account.joint_kyc)) {
      return '-';
    }

    // Use joint_account_id to find which profile's document this is
    const editedProfile = doc.account.joint_kyc.find(
      (profile: any) => profile.id === doc.joint_account_id
    );

    // If we can't find the edited profile, default to the first one
    const profileToUse = editedProfile || doc.account.joint_kyc[0];
    
    if (!profileToUse?.personal_details) {
      return '-';
    }

    return this.formatPersonName(profileToUse.personal_details);
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
